/**
 * Mirrors the enforcement state returned by the `getMfaEnforcementStatus`
 * Cloud Function. See backend/functions/src/util/mfa.ts.
 */
export interface MfaEnforcementState {
  /** Whether enforcement applies (and has started) for this user. */
  enforcementEnabled: boolean;
  /** Whether this user is currently subject to the requirement. */
  required: boolean;
  /** If not yet subject but scheduled, when enforcement begins (epoch ms). */
  requiredFrom: number | null;
  /** Whether the user currently has at least one enrolled second factor. */
  enrolled: boolean;
  /** True when trusted-domain Google SSO satisfies the requirement instead. */
  satisfiedBySso: boolean;
  /** Grace deadline as epoch milliseconds, or null if none applies. */
  graceUntil: number | null;
  /** Configured grace window in days, so copy can be phrased from config. */
  gracePeriodDays: number;
  /** Whole days remaining until the deadline (0 once passed). */
  daysRemaining: number;
  /** True when a privileged, un-enrolled user is past their deadline. */
  graceExpired: boolean;
  /** True when the user must enrol now before continuing. */
  mustEnrol: boolean;
  /** True when the user should be prompted but can still defer. */
  shouldPrompt: boolean;
}

export const MFA_TOTP_ISSUER = 'TRACit';

/** Must match DEFAULT_MFA_GRACE_PERIOD_DAYS in backend/functions/src/util/mfa.ts. */
export const DEFAULT_MFA_GRACE_PERIOD_DAYS = 7;

/**
 * Resolves a configured grace window to whole days, mirroring the backend's
 * defaulting (a missing, non-numeric or non-positive value falls back to the
 * default) so client copy never disagrees with the deadline actually stamped.
 */
export function resolveGracePeriodDays(value: unknown): number {
  const days = Number(value);
  return Number.isFinite(days) && days > 0 ? Math.floor(days) : DEFAULT_MFA_GRACE_PERIOD_DAYS;
}
